// The picture editor's working state and the edit it renders.
import type { Area } from 'react-easy-crop';
import { newCardEdit, type CardEdit, type Canvas } from '@str/shared';
import { cardSource, type ComposerCard } from './composer-cards';

export type PictureDraft = {
  edit: CardEdit;
  shape: Canvas;
  area: Area | null;
  position: { x: number; y: number };
  zoom: number;
  selected: string | null;
  editing: string | null;
  color: string;
  strokeWidth: number;
  /** Apply the adjustments to every picture when done. */
  applyToAll: boolean;
};

export function startDraft(
  card: ComposerCard,
  canvas: Canvas,
  localPhotos: ReadonlyMap<string, string>,
): PictureDraft {
  const source = cardSource(card, localPhotos);
  const edit = card.edit ?? newCardEdit(source, canvas);
  return {
    edit: { ...edit, source },
    shape: card.edit?.canvas ?? canvas,
    area: null,
    position: { x: 0, y: 0 },
    zoom: card.edit?.crop.zoom ?? 1,
    selected: null,
    editing: null,
    color: '#ffffff',
    strokeWidth: 0.012,
    applyToAll: false,
  };
}

/** The edit to render: the draft's crop when the cropper reported one, else what it opened with. */
export function draftEdit(d: PictureDraft): CardEdit {
  return {
    ...d.edit,
    canvas: d.shape,
    crop: d.area
      ? {
          x: d.area.x / 100,
          y: d.area.y / 100,
          w: d.area.width / 100,
          h: d.area.height / 100,
          zoom: d.zoom,
        }
      : d.edit.crop,
  };
}

/** Open an existing text layer directly, keeping its editable source and other layers intact. */
export function focusText(draft: PictureDraft): PictureDraft {
  const text =
    draft.edit.layers.find((l) => l.kind === 'text' && l.id === draft.selected) ??
    draft.edit.layers.find((l) => l.kind === 'text');
  return text ? { ...draft, selected: text.id, editing: text.id } : draft;
}
